// AiAutomationHubPage — index of every per-industry AI Automation page.
// Routed at /ai-automation. Groups industries by their data "group" field and
// links to each /{industry}-ai-automation page.

import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Bot, Clock, ShieldCheck, Plug } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import PageHero from "../components/shared/PageHero";
import CTABanner from "../components/homepage/CTABanner";
import { AI_AUTOMATION_LIST } from "../data/aiAutomationData";
import { getIndustryBySlug } from "../data/industryData";

const VALUE_PROPS = [
  {
    icon: Clock,
    title: "Live in 2–4 weeks",
    text: "Done-for-you build, integration and training — not a DIY tool you have to figure out.",
  },
  {
    icon: Plug,
    title: "Connects to your stack",
    text: "Reads and writes to your CRM, calendar, booking and support tools so every answer is accurate.",
  },
  {
    icon: ShieldCheck,
    title: "Guardrails & human fallback",
    text: "Trained only on your verified data, with strict guardrails and escalation to a human when needed.",
  },
];

export default function AiAutomationHubPage() {
  // Decorate each entry with its industry base info and group.
  const entries = AI_AUTOMATION_LIST.map((a) => ({
    ...a,
    industry: getIndustryBySlug(a.industrySlug),
  })).filter((a) => a.industry);

  // Group by industry.group, preserving first-seen order.
  const groups = [];
  const groupMap = {};
  entries.forEach((e) => {
    const g = e.industry.group || "Other";
    if (!groupMap[g]) {
      groupMap[g] = { title: g, items: [] };
      groups.push(groupMap[g]);
    }
    groupMap[g].items.push(e);
  });

  return (
    <>
      <Helmet>
        <title>AI Automation for Business — Industry-Specific AI Agents | Lumotis</title>
        <meta
          name="description"
          content="Lumotis builds done-for-you AI automation for 17 industries — instant lead response, 24/7 booking, support agents, no-show reduction and more. Find your industry."
        />
        <link rel="canonical" href="https://lumotis.com/ai-automation" />
        <meta property="og:title" content="AI Automation for Business | Lumotis" />
        <meta property="og:description" content="Done-for-you, industry-specific AI automation. Instant lead response, 24/7 booking, support agents and more." />
        <meta property="og:url" content="https://lumotis.com/ai-automation" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      <PageHero
        eyebrow="AI Automation"
        title="AI Automation, built for"
        titleAccent="your industry."
        subtitle="We deploy done-for-you AI agents that respond to leads in seconds, book appointments 24/7, answer customers, cut no-shows and reactivate dormant revenue — wired into the tools you already use."
        breadcrumb={[{ label: "Home", href: "/" }, { label: "AI Automation" }]}
        primaryCta={{ to: "/contact", label: "Book a Free Automation Audit", testid: "hub-cta" }}
        secondaryCta={{ to: "#industries", label: "Browse by industry" }}
      />

      {/* Value props */}
      <section className="pb-4 bg-white">
        <div className="container grid grid-cols-1 md:grid-cols-3 gap-5">
          {VALUE_PROPS.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="rounded-2xl border border-zinc-100 bg-white p-7 shadow-card"
              >
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-electric/8 border border-electric/15 mb-5">
                  <Icon className="w-5 h-5 text-electric" strokeWidth={2} />
                </span>
                <h2 className="font-display text-lg font-bold text-charcoal mb-2">{v.title}</h2>
                <p className="text-sm text-zinc-500 leading-relaxed">{v.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Industry grid */}
      <section id="industries" className="py-20 md:py-28 bg-white scroll-mt-24">
        <div className="container">
          <div className="mb-12">
            <p className="text-overline text-electric mb-3">
              {entries.length} industries
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
              Find AI automation for your business.
            </h2>
          </div>

          <div className="space-y-14">
            {groups.map((group) => (
              <div key={group.title}>
                <div className="flex items-center gap-3 mb-6">
                  <h3 className="font-display text-sm font-bold uppercase tracking-widest text-zinc-400">
                    {group.title}
                  </h3>
                  <span className="flex-1 h-px bg-zinc-100" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map((e, idx) => (
                    <motion.div
                      key={e.aiSlug}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: Math.min(idx * 0.04, 0.2) }}
                    >
                      <Link
                        to={`/${e.aiSlug}`}
                        className="group rounded-2xl border border-zinc-100 bg-white p-7 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                        data-testid={`hub-card-${e.aiSlug}`}
                      >
                        <div className="flex items-center justify-between mb-5">
                          <span className="text-3xl" aria-hidden>{e.industry.emoji}</span>
                          <Bot className="w-5 h-5 text-zinc-300 group-hover:text-electric transition-colors" />
                        </div>
                        <h4 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug transition-colors">
                          {e.industry.name} AI Automation
                        </h4>
                        <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">
                          {e.heroAccent}
                        </p>
                        <span className="mt-6 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest transition-colors">
                          Explore <ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-2xl border border-zinc-100 bg-zinc-50 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl font-bold text-charcoal">
                Don't see your industry?
              </h3>
              <p className="mt-2 text-zinc-500 leading-relaxed max-w-xl">
                We build custom AI automation for any business. Tell us your workflows and we'll map the highest-ROI automations for you.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 transition-colors shadow-lg shadow-electric/25 w-fit shrink-0"
            >
              Talk to us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABanner />
      <Footer />
    </>
  );
}
